import { ActionIcon, Badge, Tooltip } from "@mantine/core";
import IconCheck from "@src/assets/icons/tabler/check.svg?react";
import clsx from "clsx";
import type React from "react";
import { useMarkMentionAsRead } from "../mutations/mark-as-read";
import type { MentionInboxItem } from "../schema";
import { MentionUserAvatar } from "../ui/mention-user-avatar";

function formatRelativeTime(isoString: string): string {
	const diffMs = Date.now() - new Date(isoString).getTime();
	const diffMins = Math.floor(diffMs / (60 * 1000));
	if (diffMins < 1) return "Just now";
	if (diffMins < 60) return `${diffMins}m ago`;
	const diffHours = Math.floor(diffMins / 60);
	if (diffHours < 24) return `${diffHours}h ago`;
	const diffDays = Math.floor(diffHours / 24);
	return `${diffDays}d ago`;
}

function getTargetBadge(type: MentionInboxItem["targetType"]) {
	switch (type) {
		case "CARD":
			return (
				<Badge color="blue" size="xs" variant="light">
					Card
				</Badge>
			);
		case "COMMENT":
			return (
				<Badge color="grape" size="xs" variant="light">
					Comment
				</Badge>
			);
	}
}

export function MentionInboxItemComponent(props: {
	item: MentionInboxItem;
	onSelect: (item: MentionInboxItem) => void;
}) {
	const { item, onSelect } = props;
	const { mutate: markAsRead } = useMarkMentionAsRead();

	const handleCheckClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		markAsRead(item.id);
	};

	return (
		<button
			type="button"
			className={clsx(
				"group relative flex w-full cursor-pointer gap-3 border-b",
				"border-border-default/60 p-3 text-left transition-colors",
				item.isRead
					? "bg-transparent hover:bg-surface-hover-subtle opacity-75"
					: "bg-brand-primary/5 hover:bg-brand-primary/10",
			)}
			onClick={() => onSelect(item)}
		>
			{!item.isRead && (
				<span
					aria-label="Unread"
					className={clsx(
						"absolute left-1.5 top-4 size-2 rounded-full",
						"bg-brand-primary",
					)}
				/>
			)}

			<div className="shrink-0 pt-0.5 ml-1">
				<MentionUserAvatar
					avatarUrl={item.author.avatarUrl}
					name={item.author.displayName}
					size="sm"
				/>
			</div>

			<div className="flex min-w-0 flex-1 flex-col gap-1">
				<div className="flex items-center justify-between gap-1 text-xs">
					<div className="flex min-w-0 items-center gap-1.5 truncate">
						<span className="font-semibold text-text-primary truncate">
							{item.author.displayName}
						</span>
						<span className="text-text-tertiary">in</span>
						<span className="truncate font-medium text-text-secondary">
							{item.targetTitle}
						</span>
					</div>
					<span className="shrink-0 text-[11px] text-text-tertiary">
						{formatRelativeTime(item.createdAt)}
					</span>
				</div>

				<p
					className={clsx(
						"line-clamp-2 text-xs text-text-secondary",
						"font-normal leading-relaxed",
					)}
				>
					{item.snippet}
				</p>

				<div className="mt-1 flex items-center justify-between">
					<div>{getTargetBadge(item.targetType)}</div>

					{!item.isRead && (
						<Tooltip label="Mark as read" withArrow>
							<ActionIcon
								aria-label="Mark mention as read"
								className={clsx(
									"opacity-0 group-hover:opacity-100",
									"transition-opacity",
								)}
								color="gray"
								onClick={handleCheckClick}
								size="xs"
								variant="subtle"
							>
								<IconCheck className="size-3.5" />
							</ActionIcon>
						</Tooltip>
					)}
				</div>
			</div>
		</button>
	);
}
